import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Input,
  Field,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { openAIService } from '@/services/openai';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SettingsModal({ isOpen, onClose }: SettingsModalProps) {
  const [apiKey, setApiKey] = useState('');
  const [savedKey, setSavedKey] = useState<string | null>(null);

  useEffect(() => {
    const key = openAIService.getApiKey();
    setSavedKey(key);
    if (key) {
      // Show masked version
      setApiKey(maskApiKey(key));
    }
  }, [isOpen]);

  const maskApiKey = (key: string): string => {
    if (key.length <= 8) return key;
    return `${key.substring(0, 4)}${'*'.repeat(key.length - 8)}${key.substring(key.length - 4)}`;
  };

  const handleSave = () => {
    if (apiKey && !apiKey.includes('*')) {
      openAIService.setApiKey(apiKey);
      setSavedKey(apiKey);
      onClose();
    }
  };

  const handleClear = () => {
    openAIService.clearApiKey();
    setApiKey('');
    setSavedKey(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<Dismiss24Regular />}
                onClick={onClose}
              />
            }
          >
            Settings
          </DialogTitle>
          <DialogContent>
            <div className="space-y-4">
              <Field label="OpenAI API Key" required>
                <Input
                  type={savedKey && apiKey.includes('*') ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="sk-..."
                  className="w-full"
                />
                <div className="text-xs text-gray-300 mt-2">
                  Your API key is stored locally and never sent to our servers.
                  Get your API key from{' '}
                  <a
                    href="https://platform.openai.com/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-salesforce-blue hover:underline"
                  >
                    OpenAI Platform
                  </a>
                  .
                </div>
              </Field>

              {savedKey && (
                <div className="bg-success-green/10 border border-success-green/30 rounded p-3 text-sm">
                  ✓ API key is configured
                </div>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Cancel
            </Button>
            {savedKey && (
              <Button appearance="secondary" onClick={handleClear}>
                Clear Key
              </Button>
            )}
            <Button appearance="primary" onClick={handleSave}>
              Save
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
